import React, { useState, useRef, useEffect } from 'react';
import { copyMilestoneTableToClipboard } from './milestoneCopyUtils';
import { MilestoneDeleteButton } from './MilestoneDeleteButton';
import styles from './MilestoneMenu.module.scss';

interface MilestoneMenuProps {
  milestones: any[];
  momentName?: string;
  className?: string;
  isOpen?: boolean;
  onToggle?: () => void;
  onDelete?: (phaseId?: string, phaseName?: string) => void;
  phaseId?: string;
  phaseName?: string;
}

export const MilestoneMenu: React.FC<MilestoneMenuProps> = ({
  milestones,
  momentName,
  className = '',
  isOpen: controlledIsOpen,
  onToggle,
  onDelete,
  phaseId,
  phaseName
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Use controlled state if provided, otherwise use internal state
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking inside the menu
      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking the menu button
      if (buttonRef.current && buttonRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on the delete confirmation dialog or its backdrop
      const clickedDialog = target.closest('[data-dialog="true"]') !== null;

      if (clickedDialog) {
        return;
      }

      // Close the menu

      if (onToggle && isOpen) {
        onToggle();
      } else if (!onToggle) {
        setInternalIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onToggle]);

  const handleCopyMilestones = async () => {
    setIsCopying(true);
    try {
      await copyMilestoneTableToClipboard(milestones, momentName);
      // Close menu after successful copy
      if (onToggle && isOpen) {
        onToggle();
      } else if (!onToggle) {
        setInternalIsOpen(false);
      }
    } finally {
      setIsCopying(false);
    }
  };

  const handleButtonClick = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  return (
    <div className={`${styles.milestoneMenuContainer} ${className}`}>
      {/* Three Dots Button */}
      <button
        ref={buttonRef}
        className={styles.menuButton}
        onClick={handleButtonClick}
        aria-label="Milestone options"
        aria-expanded={isOpen}
        aria-haspopup="true"
        data-testid="milestone-menu-button"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="8" cy="3" r="1.5" fill="currentColor" />
          <circle cx="8" cy="8" r="1.5" fill="currentColor" />
          <circle cx="8" cy="13" r="1.5" fill="currentColor" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className={styles.dropdownMenu}
          data-testid="milestone-menu-dropdown"
        >
          <button
            className={styles.menuItem}
            onClick={handleCopyMilestones}
            disabled={isCopying || milestones.length === 0}
            data-testid="copy-milestone-option"
          >
            <span className={styles.menuItemText}>
              {isCopying ? 'Copying...' : 'Copy Milestones'}
            </span>
          </button>

          <MilestoneDeleteButton
            milestones={milestones}
            onDelete={onDelete}
            phaseId={phaseId}
            phaseName={phaseName}
            isOpen={isOpen}
            onToggle={onToggle}
            onInternalClose={() => setInternalIsOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

export default MilestoneMenu;
